import { useEffect, useRef, useState, useMemo } from 'react';

const usePrevious = <T>(value: T) => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

const useTabs = <K extends number | string>(
  tabs: K[],
  defaultTab?: K | null | undefined,
  stopStateManipulation = false,
) => {
  const state = useState<K | null>();
  const [selectedTab, setSelectedTab] = state;
  const activeIndex = useMemo(() => {
    if (selectedTab === undefined || selectedTab === null) {
      return tabs.indexOf(selectedTab);
    }

    return -1;
  }, [selectedTab, tabs]);

  const previousActiveIndex = usePrevious(activeIndex);

  useEffect(() => {
    if (tabs.length === 0) {
      setSelectedTab(undefined);

      return;
    }

    if (
      stopStateManipulation ||
      selectedTab === null ||
      (selectedTab && tabs.includes(selectedTab))
    ) {
      return;
    }

    if (
      typeof previousActiveIndex === 'number' &&
      previousActiveIndex >= 0 &&
      (tabs[previousActiveIndex] || tabs[previousActiveIndex - 1])
    ) {
      setSelectedTab(tabs[previousActiveIndex] || tabs[previousActiveIndex - 1]);

      return;
    }

    if (defaultTab === null) {
      return;
    }

    setSelectedTab(
      defaultTab !== null &&
        defaultTab !== undefined &&
        defaultTab >= 0 &&
        tabs.includes(defaultTab)
        ? defaultTab
        : undefined,
    );
  }, [
    defaultTab,
    previousActiveIndex,
    selectedTab,
    setSelectedTab,
    tabs,
    stopStateManipulation,
  ]);

  return state;
};

export default useTabs;
